import { useState, useRef } from 'react';
import { Head, useForm } from '@inertiajs/react';
import GuestLayout from '@/Layouts/GuestLayout';
import Alert from '@/Components/Alert';
import { ShieldCheckIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

export default function VerifyOtp({ email }) {
    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef([]);
    const { data, setData, post, processing, errors } = useForm({ otp: '', trust_device: true });

    const handleDigitChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;
        const newDigits = [...digits];
        newDigits[index] = value;
        setDigits(newDigits);
        setData('otp', newDigits.join(''));
        if (value && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            const newDigits = [...digits];
            newDigits[index - 1] = '';
            setDigits(newDigits);
            setData('otp', newDigits.join(''));
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData('text').trim();
        if (/^\d{6}$/.test(paste)) {
            const newDigits = paste.split('');
            setDigits(newDigits);
            setData('otp', paste);
            inputRefs.current[5]?.focus();
        }
    };

    const submit = (e) => {
        e.preventDefault();
        post('/otp/verify');
    };

    return (
        <GuestLayout>
            <Head title="Verify OTP" />
            <div className="text-center mb-5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-2.5">
                    <ShieldCheckIcon className="w-4 h-4 text-emerald-800" />
                </div>
                <h2 className="text-lg font-bold text-slate-800">Verify Identity</h2>
                <p className="text-slate-400 text-xs mt-1">Code sent to</p>
                <p className="text-slate-700 font-semibold text-xs mt-0.5">{email}</p>
            </div>

            {errors.otp && <Alert type="error" className="mb-3">{errors.otp}</Alert>}

            <form onSubmit={submit} className="space-y-4">
                <div>
                    <label className="block text-xs font-medium text-slate-600 mb-2 text-center">Enter 6-digit code</label>
                    <div className="flex justify-center gap-1.5">
                        {digits.map((digit, i) => (
                            <input key={i} ref={el => inputRefs.current[i] = el} type="text" maxLength={1} inputMode="numeric" value={digit}
                                onChange={e => handleDigitChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste}
                                className={`text-center text-base font-bold border-2 transition-colors bg-slate-50/50 focus:bg-white ${digit ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-200'} focus:border-emerald-700 focus:ring-emerald-700`}
                                style={{ borderRadius: '8px', width: '38px', height: '44px' }} />
                        ))}
                    </div>
                    <p className="text-[10px] text-slate-400 text-center mt-2">Expires in 5 minutes</p>
                </div>

                <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" checked={data.trust_device} onChange={e => setData('trust_device', e.target.checked)} className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-700" />
                        <span className="text-xs text-slate-500">Trust this device for 30 days</span>
                    </label>
                </div>

                <button type="submit" disabled={processing || data.otp.length < 6} className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                    Verify & Sign In <ArrowRightIcon className="w-4 h-4" />
                </button>
            </form>

            <div className="mt-4 flex items-center justify-between">
                <a href="/login" className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeftIcon className="w-3.5 h-3.5" /> Back</a>
                <form method="POST" action="/otp/resend"><button type="submit" className="text-xs text-emerald-800 hover:text-emerald-900 font-medium">Resend code</button></form>
            </div>
        </GuestLayout>
    );
}
